/**
 * Which staged edits a click-trace should un-stage so the traced value shows.
 *
 * The order form shows a field as edited > traced > fetched. Clearing a date
 * picker stages '' as an edit, and '' is an edit, so a field a CSR blanked on
 * screen kept hiding the value the trace had just pulled from CMS: they
 * blanked Cutoff and 1st Rcv on RICGCU258500, clicked Trace, the popup showed
 * both dates and the inputs stayed empty (2026-10-14). Same precedence on the
 * Order tab and the Tracing tab, same for import and export.
 *
 * The rule, per key the trace pulled:
 *  - CHANGED by this trace: the staged edit goes. The database moved under
 *    it, and saving the stale edit would overwrite what the trace just wrote.
 *    The highlight on the field says why.
 *  - evaluated but UNCHANGED: the staged edit goes only when it is blank, or
 *    already equals the traced value (a spurious "unsaved change"). A typed,
 *    different value stays: nothing moved, so nothing earns the right to
 *    erase it without a highlight to explain it.
 */

export type TraceOverlayResult = {
  updated_order_fields?: Record<string, unknown>;
  updated_keys?: readonly string[];
};

export type TraceOverlayEditing = {
  editedOrderFields?: Record<string, unknown>;
  revertOrderField?: (name: string) => void;
};

const norm = (v: unknown): string => (v === null || v === undefined ? '' : String(v).trim());

export function keysToUnstage(
  overlay: Record<string, unknown>,
  updatedKeys: readonly string[] | undefined,
  editedOrderFields: Record<string, unknown> | undefined,
): string[] {
  const edited = editedOrderFields ?? {};
  const changed = new Set(updatedKeys ?? []);
  const out: string[] = [];
  for (const key of Object.keys(overlay)) {
    if (!Object.prototype.hasOwnProperty.call(edited, key) || edited[key] === undefined) continue;
    const staged = norm(edited[key]);
    if (changed.has(key) || staged === '' || staged === norm(overlay[key])) out.push(key);
  }
  return out;
}

/** Apply the rule to a trace result: revert each staged edit the traced
 *  value should now show through. Returns the keys reverted. */
export function unstageTracedEdits(
  result: TraceOverlayResult,
  editing: TraceOverlayEditing | null | undefined,
): string[] {
  const overlay = result.updated_order_fields ?? {};
  const revert = editing?.revertOrderField;
  if (!revert) return [];
  const keys = keysToUnstage(overlay, result.updated_keys, editing?.editedOrderFields);
  for (const key of keys) revert(key);
  return keys;
}
